/**
 * Phase 45 Plan 45-05 — BurnBar primitive.
 *
 * Visual: emerald <90% / amber 90-100% / red over-budget with diagonal-
 * hatch over-segment clamped at 60% of bar width. Inline-style background
 * per Pill.tsx data-as-style convention (semantic colour, not Tailwind).
 *
 * Source: UI-SPEC §Surface 1 BurnBar spec + bundle manager-overview.jsx
 * lines 110-220.
 *
 * A11y: wraps fill in `role="progressbar"` with `aria-valuenow / valuemin /
 * valuemax / aria-label` per UI-SPEC §A11y Contract.
 */
import type { CSSProperties } from 'react';

export interface BurnBarProps {
  pct: number;
  height?: number;
  showLabel?: boolean;
}

function fillColour(pct: number): string {
  if (pct < 0.9) return '#10b981'; // emerald
  if (pct <= 1) return '#f59e0b'; // amber
  return '#ef4444'; // red base
}

export function BurnBar({ pct, height = 6, showLabel = false }: BurnBarProps) {
  const safePct = Number.isFinite(pct) ? Math.max(0, pct) : 0;
  const valuePct = Math.round(safePct * 100);
  const over = safePct > 1;
  const baseWidthPct = Math.min(100, safePct * 100);
  const overWidthPct = over ? Math.min(60, (safePct - 1) * 100) : 0;

  const trackStyle: CSSProperties = {
    height: `${height}px`,
    background: 'var(--ms-surface-2, #F4F1EC)',
  };

  const fillStyle: CSSProperties = {
    width: `${baseWidthPct}%`,
    background: fillColour(safePct),
  };

  const overStyle: CSSProperties = over
    ? {
        width: `${overWidthPct}%`,
        background:
          'repeating-linear-gradient(45deg, #ef4444 0 3px, #c8210e 3px 6px)',
      }
    : { width: '0', background: 'transparent' };

  const labelColour = over
    ? 'text-red-600'
    : safePct >= 0.9
    ? 'text-amber-700'
    : 'text-ms-text-strong';

  return (
    <div className="flex items-center gap-2 w-full">
      <div
        className="relative flex-1 rounded-full overflow-hidden"
        style={trackStyle}
        role="progressbar"
        aria-valuenow={valuePct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Burn vs plan"
      >
        <div className="absolute inset-y-0 left-0 h-full rounded-full" style={fillStyle} />
        {over && (
          <div
            className="absolute inset-y-0 h-full"
            style={{ ...overStyle, left: '100%', transform: 'translateX(-100%)' }}
          />
        )}
      </div>
      {showLabel && (
        <span className={`text-[11px] tabular-nums font-semibold ${labelColour}`}>
          {valuePct}%
        </span>
      )}
    </div>
  );
}
